import { Venta, VentaItem } from "@/shared/types/venta";
import { Stack, StackProps, Typography } from "@mui/material";
import dayjs from "dayjs";
import { ReactNode } from "react";
import { formatNumber, formatString } from "@/shared/formats";
import { mapMonedaValues } from "@/features/monetizacion/shared/utils";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import TicketThemeProvider from "./ticket-theme-provider";

export interface VentaInfoTicketProps extends StackProps {
    venta: Venta,
    items: VentaItem[],
    monedaId?: string,
}

export default function VentaInfoTicket({
    venta,
    items,
    monedaId,
    ...props
}: VentaInfoTicketProps) {
    const { monedaBase } = useMonetizacion();
    const { printPaddingRight, showMonedaId: configShowMonedaId, ticketHeader, ticketFooter, ticketItemIdentifier, articuloNombreMaxLength } = useConfigurationContext();
    const truncateNombre = (nombre: string) => articuloNombreMaxLength > 0 && nombre.length > articuloNombreMaxLength ? nombre.slice(0, articuloNombreMaxLength) + "..." : nombre;
    const cotizacion = venta.cotizacion;
    const showMonedaId = monedaId || configShowMonedaId || monedaBase.id;

    const total = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);
    const showMonedaSimbolo = cotizacion.cotizacionItems.find(ci => ci.monedaId === showMonedaId)?.monedaSimbolo ?? "";
    const otrasMonedas = cotizacion.cotizacionItems.filter(ci => ci.monedaId !== showMonedaId);

    return <TicketThemeProvider>
        <Stack gap={2} paddingRight={`${printPaddingRight}mm`} {...props}>
            {ticketHeader && <>
                <Stack gap={1} alignItems="center">
                    <Typography variant="body2" textAlign="center" whiteSpace="pre-line">{ticketHeader}</Typography>
                </Stack>
                <DashedDivider />
            </>}

            {/* Header */}
            <Stack gap={1}>
                <AttributeValuePair label="Ticket #: " value={venta.numeroVenta} />
                <AttributeValuePair label="Fecha: " value={dayjs(venta.fecha).format("DD [de] MMMM [del] YYYY - HH:mm")} />
                {venta.presupuesto ?
                    <AttributeValuePair label="Presupuesto" value="" /> :
                    <AttributeValuePair label="Condición de Venta: " value={venta.credito ? "Crédito" : "Contado"} />}
                <AttributeValuePair label="Cliente: " value={venta.clienteNombre} />
                <AttributeValuePair label="RUC: " value={venta.clienteNumDocumento} />
                <AttributeValuePair label="Vendedor/a: " value={venta.vendedorNombre} />
            </Stack>

            <DashedDivider />

            {/* Body */}
            <Stack gap={1}>
                <Stack gap={0}>
                    <Typography variant="body2" fontWeight="bold">{ticketItemIdentifier === "numItem" ? "NÚM. ÍTEM" : "COD"} - DESCRIPCIÓN</Typography>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight="bold">CANT.</Typography>
                        <Typography variant="body2" fontWeight="bold">MON.</Typography>
                        <Typography variant="body2" fontWeight="bold">P.U.</Typography>
                        <Typography variant="body2" fontWeight="bold">SUBT.</Typography>
                    </Stack>
                </Stack>
                <Stack borderBottom="1px dashed #000000" />
                {items.map((row) => (
                    <Stack key={row.id} gap={0}>
                        <Typography variant="body2">{ticketItemIdentifier === "numItem" ? row.articuloNumeroItem : row.articuloCodigo} — {formatString(truncateNombre(row.articuloNombre))}</Typography>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ minWidth: 0, flexShrink: 1 }}>{formatNumber(row.cantidad)}</Typography>
                            <Typography variant="body2" sx={{ minWidth: 0, flexShrink: 1 }}>{showMonedaSimbolo}</Typography>
                            <Typography variant="body2" sx={{ minWidth: 0, flexShrink: 1 }}>{formatNumber(mapMonedaValues(cotizacion, row.precioUnitario, monedaBase.id, showMonedaId))}</Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 0, flexShrink: 1 }}>{formatNumber(mapMonedaValues(cotizacion, row.precioUnitario * row.cantidad, monedaBase.id, showMonedaId))}</Typography>
                        </Stack>
                    </Stack>
                ))}
            </Stack>

            <Stack gap={0.5}>
                <DashedDivider />
                <AttributeValuePair label="CANT. TOTAL DE ITEMS: " value={items.reduce((acc, row) => acc + row.cantidad, 0)} />
                <DashedDivider />
            </Stack>

            <Stack gap={1}>
                <AttributeValuePair variant="bold" label={`Total ${showMonedaSimbolo}: `} value={formatNumber(total)} />
                {otrasMonedas.map(ci => (
                    <AttributeValuePair
                        key={ci.monedaId}
                        label={`Total ${ci.monedaSimbolo}: `}
                        value={formatNumber(mapMonedaValues(cotizacion, venta.total, monedaBase.id, ci.monedaId))}
                    />
                ))}
            </Stack>

            {ticketFooter && <>
                <DashedDivider />
                <Stack gap={1} alignItems="center">
                    <Typography variant="body2" textAlign="center" whiteSpace="pre-line">{ticketFooter}</Typography>
                </Stack>
            </>}
        </Stack>
    </TicketThemeProvider>
}

function AttributeValuePair({
    label,
    value,
    variant = "normal",
    ...props
}: {
    label?: ReactNode,
    value?: ReactNode,
    variant?: "normal" | "bold"
} & StackProps) {
    return <Stack direction={"row"} justifyContent="space-between" {...props}>
        <Typography fontWeight={variant} variant={"body2"}>{label}</Typography>
        <Typography fontWeight={variant} variant={"body2"}>{value == null ? "---" : value}</Typography>
    </Stack>
}

function DashedDivider() {
    return <Stack borderBottom={"1px dashed #000000"} />
}